@if (store.loadError()) {
  <div class="flex items-center justify-between gap-4 rounded-lg bg-status-neutral p-4">
    <span>{{ 'installations.dataSources.failed' | translate }}</span>
    <xemex-text-button icon="refresh" (click)="retry()">
      {{ 'installations.insights.retry' | translate }}
    </xemex-text-button>
  </div>
} @else {
  <div class="flex flex-col gap-6">
    <section class="data-sources__profile" data-testid="profile-card">
      <div class="data-sources__profile-main">
        <span class="data-sources__profile-icon" aria-hidden="true">
          <mat-icon>tune</mat-icon>
        </span>
        <div class="min-w-0">
          <p class="data-sources__profile-label">
            {{ 'installations.general.installationProfile' | translate }}
          </p>
          <p class="data-sources__profile-name" data-testid="profile-name">
            {{ installation().profile?.presetName ?? ('installations.profile.custom' | translate) }}
            @if (installation().profile?.editedSincePreset) {
              <span class="data-sources__profile-edited">
                · {{ 'installations.profile.edited' | translate }}</span
              >
            }
          </p>
        </div>
        @if (canEditProfile()) {
          <xemex-tonal-button class="ml-auto" icon="swap_horiz" (click)="editProfile()">
            {{ 'installations.dataSources.changeProfile' | translate }}
          </xemex-tonal-button>
        }
      </div>
      <div class="data-sources__required" data-testid="required-bound">
        <div class="data-sources__required-head">
          <span>{{ 'installations.dataSources.requiredBound' | translate }}</span>
          @if (!store.isLoading()) {
            <span class="data-sources__required-count">
              <strong>{{ requiredBound().bound }}</strong> / {{ requiredBound().total }}
            </span>
          }
        </div>
        <xemex-progress-bar
          [value]="requiredBound().bound"
          [max]="requiredBound().total"
          [ariaLabel]="'installations.dataSources.requiredBound' | translate"
        />
      </div>
    </section>

    <xemex-card [title]="'installations.general.points' | translate">
      <div cardHeaderActions class="flex items-center gap-3">
        <mat-slide-toggle
          class="slide-toggle-sm"
          [checked]="store.showHidden()"
          (change)="store.setShowHidden($event.checked)"
          >{{ 'installations.dataSources.showRemoved' | translate }}</mat-slide-toggle
        >
        @if (canAddPoint()) {
          <xemex-text-button icon="add" iconPosition="start" (click)="addPoint()">
            {{ 'installations.dataSources.addPoint' | translate }}
          </xemex-text-button>
        }
      </div>
      @if (!store.isLoading() && pointRows().length === 0) {
        <p class="m-0 text-text-secondary">
          {{ 'installations.insights.noPoints' | translate }}
        </p>
      } @else {
        <xemex-table
          [data]="pointRows()"
          [columns]="pointColumns"
          [groups]="pointGroups"
          [loading]="store.isLoading() || (store.showHidden() && store.isLoadingAllPoints())"
          [pageable]="false"
          [compact]="true"
          [showGroupCounts]="false"
          [outlinedIcons]="true"
          actionsWidth="4.5rem"
        >
          <ng-template #actionsCell let-row>
            @if (row.actions.length) {
              <button
                mat-icon-button
                type="button"
                class="data-sources__point-menu"
                [attr.aria-label]="'installations.dataSources.pointActions' | translate"
                [matMenuTriggerFor]="pointMenu"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #pointMenu="matMenu">
                @for (action of row.actions; track action) {
                  <button mat-menu-item type="button" (click)="openAction(action, row.point)">
                    {{ 'installations.dataSources.actions.' + action | translate }}
                  </button>
                }
              </mat-menu>
            }
          </ng-template>
        </xemex-table>
        @if (!store.isLoading() && pointRows().length) {
          <p class="data-sources__showing">
            {{
              'installations.dataSources.showing'
                | translate: { from: 1, to: pointRows().length, total: pointRows().length }
            }}
          </p>
        }
      }
      @if (store.showHidden() && store.allPointsFailed()) {
        <p class="mt-3 text-text-error" role="alert">
          {{ 'installations.dataSources.hiddenFailed' | translate }}
        </p>
      }
    </xemex-card>

    <xemex-card
      [title]="'installations.dataSources.devices' | translate"
      [loading]="store.isLoading()"
    >
      @if (deviceCards().length) {
        <ul class="data-sources__devices">
          @for (device of deviceCards(); track device.deviceId) {
            <li class="data-sources__device">
              <div class="data-sources__device-head">
                <span class="data-sources__device-icon">
                  <mat-icon>memory</mat-icon>
                </span>
                <div class="min-w-0">
                  <a
                    class="data-sources__device-ldn text-text-link"
                    [routerLink]="['/', APP_PATHS.Devices, device.deviceId, 'general']"
                    >{{ device.ldn }}</a
                  >
                  @if (device.typeName) {
                    <div class="data-sources__device-type">{{ device.typeName }}</div>
                  }
                </div>
              </div>
              <dl class="data-sources__facts">
                @if (device.status) {
                  <div>
                    <dt>{{ 'installations.dataSources.device.status' | translate }}</dt>
                    <dd class="data-sources__status" [attr.data-status]="device.status.kind">
                      <span class="data-sources__status-dot"></span>
                      {{ device.status.label }}
                    </dd>
                  </div>
                }
                <div>
                  <dt>{{ 'installations.dataSources.device.channels' | translate }}</dt>
                  <dd>
                    {{
                      'installations.dataSources.boundOf'
                        | translate: { bound: device.boundChannels, total: device.channels }
                    }}
                  </dd>
                </div>
              </dl>
              @if (device.lastSeen) {
                <p class="data-sources__last-seen">{{ device.lastSeen }}</p>
              }
            </li>
          }
        </ul>
      } @else {
        <p class="text-text-secondary">{{ 'installations.dataSources.noDevices' | translate }}</p>
      }
    </xemex-card>
  </div>
}
